import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Auto-scales its child content down (never up) to fit within a max height,
 * preserving the child's original dimensions in the DOM (so html2canvas still
 * captures the full-size card unaltered).
 *
 * maxHeight can be a fixed number (px) or a function of viewport height.
 * By default uses min(560, 55vh) so buttons/footer below stay visible on
 * mobile and desktop.
 */
const AutoFitPreview = ({ maxHeight, children }) => {
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [innerHeight, setInnerHeight] = useState(0);
  const [viewportH, setViewportH] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800
  );

  const effectiveMaxH = (() => {
    if (typeof maxHeight === "number") return maxHeight;
    return Math.min(560, Math.max(320, Math.floor(viewportH * 0.55)));
  })();

  const recompute = () => {
    const el = innerRef.current;
    if (!el) return;
    // offsetHeight ignores CSS transforms → returns the natural (unscaled) height
    const h = el.offsetHeight;
    setInnerHeight(h);
    const s = h > 0 && h > effectiveMaxH ? effectiveMaxH / h : 1;
    setScale(s);
  };

  useLayoutEffect(() => {
    recompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, effectiveMaxH, viewportH]);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const onResize = () => {
      setViewportH(window.innerHeight);
      recompute();
    };
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => recompute());
      ro.observe(el);
    }
    window.addEventListener("resize", onResize);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scaledHeight = innerHeight ? innerHeight * scale : undefined;

  return (
    <div
      className="w-full"
      style={{
        height: scaledHeight ? `${scaledHeight}px` : undefined,
        overflow: "hidden",
      }}
    >
      <div
        ref={innerRef}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top center",
          width: "100%",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default AutoFitPreview;
